import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import {
  Brain, ChevronRight, X, Loader2, Search, AlertTriangle, ShieldOff,
  Clock, Package, MessageSquare, Ban, CheckCircle2, TrendingUp, User, Settings as SettingsIcon,
  Smile, RotateCcw, RefreshCcw, BarChart3,
} from 'lucide-react';
import AnalyticsPanel from './sales_brain/AnalyticsPanel';

// Phase 7 · Sales Brain Dashboard — READ-ONLY. Feb 2026.
// Renders sales_opportunities enriched by every prior Sales Brain phase.

const PRIORITY_STYLE = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-200',
  HIGH:     'bg-orange-100 text-orange-700 border-orange-200',
  MEDIUM:   'bg-amber-100 text-amber-700 border-amber-200',
  LOW:      'bg-neutral-100 text-neutral-600 border-neutral-200',
  NONE:     'bg-neutral-50 text-neutral-500 border-neutral-200',
};
const INTENT_STYLE = {
  PURCHASE_INTENT: 'bg-emerald-100 text-emerald-700',
  ORDER_READY:     'bg-emerald-100 text-emerald-700',
  PAYMENT_READY:   'bg-teal-100 text-teal-700',
  REORDER_INTENT:  'bg-cyan-100 text-cyan-700',
  PRODUCT_INTEREST:'bg-fab-primary-lighter text-fab-primary',
  PRICE_ENQUIRY:   'bg-fab-primary-lighter text-fab-primary',
  MOQ_ENQUIRY:     'bg-fab-primary-lighter text-fab-primary',
  STOCK_ENQUIRY:   'bg-fab-primary-lighter text-fab-primary',
  NOT_READY:       'bg-neutral-100 text-neutral-600',
  DISCOVERY:       'bg-neutral-100 text-neutral-600',
  COMPLAINT:       'bg-red-100 text-red-700',
  SUPPORT:         'bg-blue-100 text-blue-700',
  UNKNOWN:         'bg-neutral-50 text-neutral-500',
};

function Chip({ children, className = '', ...rest }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-transparent ${className}`} {...rest}>
      {children}
    </span>
  );
}

function EmptyBadge({ children = '—' }) {
  return <span className="text-neutral-400 text-xs">{children}</span>;
}

function DetailBlock({ title, icon: Icon, children, testId }) {
  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        {Icon ? <Icon className="w-4 h-4 text-fab-primary" /> : null}
        <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
      </div>
      <div className="space-y-1.5 text-sm">{children}</div>
    </section>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-neutral-500">{k}</span>
      <span className="text-neutral-900 text-right break-words max-w-[65%]">
        {v == null || v === '' ? <EmptyBadge /> : String(v)}
      </span>
    </div>
  );
}

// Phase 12 · Learning Signals — read-only. Consumes the shared learning
// summary payload (one bounded API call) and filters locally to buckets
// relevant to the current opportunity. Zero action controls.
function LearningSignalsBlock({ detail, learning }) {
  const items = (learning && Array.isArray(learning.items)) ? learning.items : [];
  const relevantKeys = React.useMemo(() => {
    const out = [];
    if (detail?.next_best_action)
      out.push({ event_type: 'NBA', event_key: detail.next_best_action });
    if (detail?.recommendation_status)
      out.push({ event_type: 'RECOMMENDATION',
                  event_key: detail.recommended_product_code || detail.recommendation_status });
    if (detail?.objection_type)
      out.push({ event_type: 'OBJECTION', event_key: detail.objection_type });
    if (detail?.reorder_status)
      out.push({ event_type: 'REORDER', event_key: detail.reorder_status });
    return out;
  }, [detail]);
  const matched = relevantKeys
    .map(k => items.find(i => i.event_type === k.event_type && i.event_key === k.event_key))
    .filter(Boolean);
  const topBuckets = matched.length
    ? matched
    : items.slice(0, 5);  // fallback: 5 most-observed buckets for the seller
  return (
    <DetailBlock title="Learning Signals" icon={BarChart3} testId="sb-detail-learning">
      <div className="text-[11px] text-neutral-500 pb-2 leading-snug">
        Seller historical observations. This describes past outcomes, not
        this buyer or a future prediction. Never overrides live order,
        stock, price or policy data.
      </div>
      {learning === null ? (
        <EmptyBadge>Learning summary unavailable</EmptyBadge>
      ) : topBuckets.length === 0 ? (
        <EmptyBadge>No observations yet</EmptyBadge>
      ) : (
        <div className="space-y-2" data-testid="sb-learning-buckets">
          {topBuckets.map((b, i) => (
            <div key={`${b.event_type}-${b.event_key}-${i}`}
                 className="border-t border-neutral-100 pt-2 first:border-0 first:pt-0"
                 data-testid={`sb-learning-${b.event_type}-${b.event_key}`}>
              <div className="flex justify-between items-baseline">
                <span className="text-[12px] font-medium text-neutral-800">
                  {b.event_type}: {b.event_key || '—'}
                </span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  b.confidence === 'HIGH' ? 'bg-emerald-50 text-emerald-700' :
                  b.confidence === 'MEDIUM' ? 'bg-amber-50 text-amber-700' :
                  b.confidence === 'LOW' ? 'bg-neutral-100 text-neutral-600' :
                  'bg-neutral-50 text-neutral-500'
                }`}>{b.confidence || 'UNKNOWN'}</span>
              </div>
              <KV k="Observations" v={b.observations} />
              <KV k="Successful outcomes" v={b.successes} />
              <KV k="Failure outcomes" v={b.failures} />
              <KV k="Unknown outcomes" v={b.unknown} />
              <KV k="Observed success rate" v={
                b.observed_success_rate_percent == null
                  ? 'Insufficient data'
                  : `${b.observed_success_rate_percent}%`
              } />
              <KV k="Attribution available" v={b.attribution_available} />
              <KV k="Attribution unavailable" v={b.attribution_unavailable} />
            </div>
          ))}
        </div>
      )}
    </DetailBlock>
  );
}

export default function SalesBrain() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [priority, setPriority] = useState('');
  const [intent, setIntent] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [selected, setSelected] = useState(null);   // buyer_key of selected row
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [learning, setLearning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (priority) params.set('priority', priority);
      if (intent) params.set('intent', intent);
      if (followUp) params.set('follow_up_status', followUp);
      params.set('page', page); params.set('limit', limit);
      const { data } = await api.get(`/sales-brain/opportunities?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]); setTotal(0);
    } finally { setLoading(false); }
  }, [q, priority, intent, followUp, page, limit]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (buyerKey) => {
    setSelected(buyerKey);
    setDetailLoading(true);
    try {
      // Phase 12 · fetch the opp detail and this seller's learning summary
      // in parallel — one bounded seller-scoped call each. No N+1.
      const [{ data }, learn] = await Promise.all([
        api.get(`/sales-brain/opportunities/${encodeURIComponent(buyerKey)}`),
        api.get('/sales-brain/learning/summary?range=30d').catch(() => ({ data: null })),
      ]);
      setDetail(data);
      setLearning(learn?.data || null);
    } catch { setDetail(null); setLearning(null); }
    finally { setDetailLoading(false); }
  };
  const closeDetail = () => { setSelected(null); setDetail(null); setLearning(null); };

  return (
    <div className="space-y-4" data-testid="sales-brain-page">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <Brain className="w-6 h-6 text-fab-primary" />
            Sales Brain
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Read-only intelligence pipeline for every active buyer opportunity.
          </p>
        </div>
        <Chip className="bg-amber-50 text-amber-800 border-amber-200" data-testid="sb-readonly-badge">
          Read-only
        </Chip>
      </header>

      {/* Phase 8 — configure policies link */}
      <Link to="/dashboard/settings#sales-brain-policies"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-fab-primary-lighter bg-white hover:bg-fab-primary-lighter/40 text-[13px] text-fab-primary w-fit"
            data-testid="sb-configure-policies-link">
        <SettingsIcon className="w-4 h-4" />
        Configure Sales Brain Policies
      </Link>

      {/* Phase 9 — read-only analytics */}
      <AnalyticsPanel />

      {/* Filters */}
      <div className="bg-white rounded-xl border border-neutral-200 p-3 flex flex-wrap gap-2 items-center" data-testid="sb-filters">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-neutral-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            data-testid="sb-search"
            className="w-full pl-8 pr-2 py-2 text-sm rounded-lg border border-neutral-200 focus:border-fab-primary focus:outline-none"
            placeholder="Search buyer name, phone or session"
            value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
          />
        </div>
        <select data-testid="sb-priority-filter" className="px-2 py-2 text-sm rounded-lg border border-neutral-200"
                value={priority} onChange={(e) => { setPage(1); setPriority(e.target.value); }}>
          <option value="">All priorities</option>
          {['CRITICAL','HIGH','MEDIUM','LOW','NONE'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select data-testid="sb-intent-filter" className="px-2 py-2 text-sm rounded-lg border border-neutral-200"
                value={intent} onChange={(e) => { setPage(1); setIntent(e.target.value); }}>
          <option value="">All intents</option>
          {['PURCHASE_INTENT','ORDER_READY','PAYMENT_READY','REORDER_INTENT','PRODUCT_INTEREST',
            'PRICE_ENQUIRY','MOQ_ENQUIRY','STOCK_ENQUIRY','NOT_READY','DISCOVERY','COMPLAINT','SUPPORT'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select data-testid="sb-followup-filter" className="px-2 py-2 text-sm rounded-lg border border-neutral-200"
                value={followUp} onChange={(e) => { setPage(1); setFollowUp(e.target.value); }}>
          <option value="">Any follow-up</option>
          {['CANDIDATE','PLANNED','BLOCKED','COMPLETED','CANCELLED','EXPIRED','DO_NOT_CONTACT'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Pipeline list */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden" data-testid="sb-list">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-neutral-500 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading opportunities…
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-neutral-500" data-testid="sb-empty">
            <Brain className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
            No opportunities match these filters yet.
          </div>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {items.map(op => (
              <li key={op.buyer_key}
                  className="p-4 hover:bg-neutral-50 cursor-pointer flex items-start gap-3"
                  onClick={() => openDetail(op.buyer_key)}
                  data-testid={`sb-row-${op.buyer_key}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-neutral-900 truncate">
                      {op.buyer_name || op.buyer_phone || op.buyer_key}
                    </span>
                    {op.nba_priority && (
                      <Chip className={PRIORITY_STYLE[op.nba_priority] || PRIORITY_STYLE.NONE}>
                        {op.nba_priority}
                      </Chip>
                    )}
                    {op.current_intent && (
                      <Chip className={INTENT_STYLE[op.current_intent] || INTENT_STYLE.UNKNOWN}>
                        {op.current_intent}
                      </Chip>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-neutral-600">
                    {op.next_best_action && (
                      <span className="inline-flex items-center gap-1">
                        <TrendingUp className="w-3.5 h-3.5" />NBA: {op.next_best_action}
                      </span>
                    )}
                    {op.recommended_product_code && (
                      <span className="inline-flex items-center gap-1">
                        <Package className="w-3.5 h-3.5" />{op.recommended_product_code}
                      </span>
                    )}
                    {op.objection_type && op.objection_type !== 'NONE' && (
                      <span className="inline-flex items-center gap-1 text-red-600">
                        <AlertTriangle className="w-3.5 h-3.5" />{op.objection_type}
                      </span>
                    )}
                    {op.follow_up_status && op.follow_up_status !== 'NONE' && (
                      <span className={`inline-flex items-center gap-1 ${op.follow_up_status === 'DO_NOT_CONTACT' ? 'text-red-600' : ''}`}>
                        {op.follow_up_status === 'DO_NOT_CONTACT'
                          ? <ShieldOff className="w-3.5 h-3.5" />
                          : <Clock className="w-3.5 h-3.5" />}
                        {op.follow_up_status}
                        {op.follow_up_target_at ? ` · ${new Date(op.follow_up_target_at).toLocaleDateString()}` : ''}
                      </span>
                    )}
                    {op.last_activity_at && (
                      <span className="text-neutral-400">
                        Last active {new Date(op.last_activity_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-400 mt-1 shrink-0" />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between text-sm text-neutral-500">
          <span>Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</span>
          <div className="flex gap-2">
            <button data-testid="sb-prev" disabled={page === 1}
                    className="px-3 py-1 rounded-lg border border-neutral-200 disabled:opacity-40"
                    onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button>
            <button data-testid="sb-next" disabled={page * limit >= total}
                    className="px-3 py-1 rounded-lg border border-neutral-200 disabled:opacity-40"
                    onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={closeDetail} data-testid="sb-drawer-overlay">
          <div className="w-full sm:max-w-[560px] h-full bg-fab-base overflow-y-auto"
               onClick={(e) => e.stopPropagation()}
               data-testid="sb-drawer">
            <div className="sticky top-0 bg-white border-b border-neutral-200 px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-xs text-neutral-500">Opportunity detail</div>
                <div className="font-semibold">{detail?.buyer_name || detail?.buyer_phone || selected}</div>
              </div>
              <button data-testid="sb-drawer-close" onClick={closeDetail}
                      className="p-2 rounded-lg hover:bg-neutral-100"><X className="w-4 h-4" /></button>
            </div>
            {detailLoading || !detail ? (
              <div className="flex items-center justify-center py-16 text-neutral-500 gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : (
              <div className="p-4 space-y-3">
                <DetailBlock title="Customer" icon={User} testId="sb-detail-customer">
                  <KV k="Name"          v={detail.buyer_name} />
                  <KV k="Phone"         v={detail.buyer_phone} />
                  <KV k="Session"       v={detail.buyer_key} />
                  <KV k="Relationship"  v={detail.customer?.relationship_state} />
                  <KV k="Interactions"  v={detail.customer?.interaction_count} />
                  <KV k="First seen"    v={detail.customer?.first_interaction_at} />
                  <KV k="Last seen"     v={detail.customer?.last_interaction_at} />
                </DetailBlock>

                <DetailBlock title="Current intent" icon={MessageSquare} testId="sb-detail-intent">
                  <KV k="Intent"        v={detail.current_intent} />
                  <KV k="Strength"      v={detail.intent_strength} />
                  <KV k="Opportunity"   v={detail.opportunity_state} />
                  <KV k="Quantity"      v={detail.explicit_quantity} />
                  <KV k="Reasons"       v={(detail.reasons || []).join(', ')} />
                </DetailBlock>

                <DetailBlock title="Next Best Action" icon={TrendingUp} testId="sb-detail-nba">
                  <KV k="Action"        v={detail.next_best_action} />
                  <KV k="Priority"      v={detail.nba_priority} />
                  <KV k="Reason"        v={detail.nba_reason} />
                  <KV k="Blocking"      v={detail.nba_blocking_factor} />
                  <KV k="Evidence"      v={(detail.nba_evidence || []).join(', ')} />
                </DetailBlock>

                <DetailBlock title="Product recommendation" icon={Package} testId="sb-detail-recommendation">
                  <KV k="Status"        v={detail.recommendation_status} />
                  <KV k="Product code"  v={detail.recommended_product_code} />
                  <KV k="Confidence"    v={detail.recommendation_confidence} />
                  <KV k="Matched"       v={(detail.recommendation_matched || []).join(', ')} />
                  <KV k="Reason"        v={detail.recommendation_reason} />
                </DetailBlock>

                <DetailBlock title="Objection" icon={AlertTriangle} testId="sb-detail-objection">
                  <KV k="Type"          v={detail.objection_type} />
                  <KV k="Resolution"    v={detail.objection_resolution_status} />
                  <KV k="Reason"        v={detail.objection_reason} />
                  <KV k="Verified facts" v={(detail.objection_verified_facts || []).join(', ')} />
                  <KV k="Blocking"      v={detail.objection_blocking_factor} />
                  <KV k="Handoff?"      v={detail.objection_seller_handoff_required ? 'Yes' : 'No'} />
                  <KV k="Confidence"    v={detail.objection_confidence} />
                </DetailBlock>

                <DetailBlock title="Follow-up plan" icon={Clock} testId="sb-detail-followup">
                  <KV k="Status"        v={detail.follow_up_status} />
                  <KV k="Type"          v={detail.follow_up_candidate_type} />
                  <KV k="Timing"        v={detail.follow_up_timing_type} />
                  <KV k="Target at"     v={detail.follow_up_target_at} />
                  <KV k="Eligible"      v={detail.follow_up_eligible ? 'Yes' : 'No'} />
                  <KV k="Do not push"   v={detail.follow_up_do_not_push ? 'Yes' : 'No'} />
                  <KV k="Permission"    v={detail.follow_up_permission_state} />
                  <KV k="Blocking"      v={detail.follow_up_blocking_factor} />
                  <KV k="Stop conditions" v={(detail.follow_up_stop_conditions || []).join(', ')} />
                  <KV k="Confidence"    v={detail.follow_up_confidence} />
                  <KV k="Reason"        v={detail.follow_up_reason} />
                </DetailBlock>

                <DetailBlock title="Execution history (last 5)" icon={CheckCircle2} testId="sb-detail-execution">
                  {(detail.follow_up_execution_history || []).slice(-5).reverse().map((h, i) => (
                    <div key={i} className="border-t border-neutral-100 pt-2 first:border-0 first:pt-0">
                      <KV k="At"        v={h.at} />
                      <KV k="Status"    v={h.status} />
                      <KV k="Sent?"     v={h.message_sent ? 'Yes' : 'No'} />
                      <KV k="Reason"    v={h.reason} />
                      <KV k="Blocking"  v={h.blocking_factor} />
                      <KV k="Failure"   v={h.failure_reason} />
                    </div>
                  ))}
                  {(detail.follow_up_execution_history || []).length === 0 && (
                    <EmptyBadge>No prior execution attempt</EmptyBadge>
                  )}
                </DetailBlock>

                {/* Phase 10A — Satisfaction (read-only) */}
                <DetailBlock title="Satisfaction" icon={Smile} testId="sb-detail-satisfaction">
                  <KV k="Last severity"   v={detail.satisfaction_last_severity} />
                  <KV k="Signals"         v={(detail.satisfaction_last_signal_types || []).join(', ')} />
                  <KV k="Reason"          v={detail.satisfaction_last_signal_reason} />
                  <KV k="Last signal at"  v={detail.satisfaction_last_signal_at} />
                  <KV k="Unresolved?"     v={(detail.satisfaction_last_signal_types || []).includes('UNRESOLVED_DISSATISFACTION') ? 'Yes' : 'No'} />
                  <KV k="DNC?"            v={detail.customer?.permissions?.do_not_contact ? 'Yes' : 'No'} />
                </DetailBlock>

                {/* Phase 10A — Reactivation (read-only) */}
                <DetailBlock title="Reactivation" icon={RefreshCcw} testId="sb-detail-reactivation">
                  <KV k="Status"          v={detail.reactivation_status} />
                  <KV k="Reason"          v={detail.reactivation_reason} />
                  <KV k="Details"         v={(detail.reactivation_reason_details || []).join(' · ')} />
                  <KV k="Relationship"    v={detail.customer?.relationship_state} />
                  <KV k="DNC?"            v={detail.customer?.permissions?.do_not_contact ? 'Yes' : 'No'} />
                  <KV k="Marketing perm?" v={detail.customer?.permissions?.marketing_permission === false ? 'Denied' : 'Allowed'} />
                  <KV k="Updated at"      v={detail.reactivation_updated_at} />
                </DetailBlock>

                {/* Phase 10A — Reorder (read-only) */}
                <DetailBlock title="Reorder" icon={RotateCcw} testId="sb-detail-reorder">
                  <KV k="Status"          v={detail.reorder_status} />
                  <KV k="Reason"          v={detail.reorder_reason} />
                  <KV k="Product code"    v={detail.reorder_referenced_product_code} />
                  <KV k="Matched from"    v={detail.reorder_matched_from} />
                  <KV k="Verified"        v={(detail.reorder_verified_facts || []).join(', ')} />
                  <KV k="Blocking"        v={detail.reorder_blocking_factor} />
                  <KV k="Updated at"      v={detail.reorder_updated_at} />
                </DetailBlock>

                {/* Phase 12 — Learning Signals (read-only, seller-scoped) */}
                <LearningSignalsBlock detail={detail} learning={learning} />

                <div className="text-[11px] text-neutral-400 flex items-center gap-1 pt-2">
                  <Ban className="w-3 h-3" /> This dashboard is read-only. Sales Brain cannot be modified here.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
